// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { ButtonProps } from '@mui/material';
import { CommercialAgreement } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { CommercialAgreementOffer } from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/Offer';
import { ContractId } from '@daml/types';
import { useParties } from '../../contexts/PartiesContext';
import { useAcceptCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useAcceptCommercialAgreementOffer';
import { useCancelCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useCancelCommercialAgreementOffer';
import { useCommercialAgreementLockCoin } from '../../hooks/mutations/commercialAgreements/useCommercialAgreementLockCoin';
import { useModifyCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useModifyCommercialAgreement';
import { useRejectCommercialAgreementOffer } from '../../hooks/mutations/commercialAgreements/useRejectCommercialAgreementOffer';
import { useRevokeCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useRevokeCommercialAgreement';
import useMutate from '../../hooks/mutations/useMutate';
import {
  ERR_CANCEL_COMMERCIAL_AGREEMENT,
  ERR_MODIFY_COMMERCIAL_AGREEMENT,
  SUC_CANCEL_COMMERCIAL_AGREEMENT,
  SUC_MODIFY_COMMERCIAL_AGREEMENT,
  SUC_REJECT_COMMERCIAL_AGREEMENT,
  ERR_REJECT_COMMERCIAL_AGREEMENT,
  SUC_REVOKE_COMMERCIAL_AGREEMENT,
  ERR_REVOKE_COMMERCIAL_AGREEMENT,
  ERR_TOPUP_COMMERCIAL_AGREEMENT,
  SUC_TOPUP_COMMERCIAL_AGREEMENT,
  SUC_ACCEPT_COMMERCIAL_AGREEMENT,
  ERR_ACCEPT_COMMERCIAL_AGREEMENT,
} from '../../utils/strings';

interface Payload {
  operator: string;
  user: string;
  feeReceiver: string;
  utilityFees: string;
  ratePerDay?: string;
  billingPeriodMinutes?: string;
}

export type Row = {
  contractId: string;
  state: string;
  payload: Payload;
  reason?: string;
};
type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
  buttonProps?: ButtonProps;
};

export const useCreateCommercialAgreementActions = () => {
  const { party, operator } = useParties();
  const isOperator = operator === party;
  const mutate = useMutate();
  const revokeCommercialAgreement = useRevokeCommercialAgreement();
  const modifyCommercialAgreement = useModifyCommercialAgreement();
  const commercialAgreementLockCoin = useCommercialAgreementLockCoin();
  const rejectCommercialAgreementOffer = useRejectCommercialAgreementOffer();
  const acceptCommercialAgreementOffer = useAcceptCommercialAgreementOffer();
  const cancelCommercialAgreementOffer = useCancelCommercialAgreementOffer();
  const [onReject, setOnReject] = React.useState<
    ((reason: string) => Promise<unknown>) | undefined
  >();
  const [onModify, setOnModify] = React.useState<
    | ((
        feeReceiver: string,
        utilityFees: string,
        billingPeriodMinutes?: string,
        rate?: string,
      ) => Promise<unknown>)
    | undefined
  >();
  const [onChangeTopupAmount, setOnChangeTopupAmount] = React.useState<
    ((amount: string) => Promise<unknown>) | undefined
  >();
  const [onRevoke, setOnRevoke] = React.useState<(() => Promise<unknown>) | undefined>();
  const [currFeeReceiver, setCurrFeeReceiver] = React.useState('');
  const [currUtilityFees, setCurrUtilityFees] = React.useState('');
  const [currRate, setCurrRate] = React.useState<string | undefined>();
  const [currBillingPeriodMinutes, setCurrBillingPeriodMinutes] = React.useState<
    string | undefined
  >();
  const rejectCommercialAgreementOff = React.useCallback(
    (row: Row, reason: string) => {
      const payload = {
        commercialAgreementOfferCid: row.contractId as ContractId<CommercialAgreementOffer>,
        reason,
      };
      return mutate(
        rejectCommercialAgreementOffer,
        payload,
        SUC_REJECT_COMMERCIAL_AGREEMENT,
        ERR_REJECT_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, rejectCommercialAgreementOffer],
  );
  const modifyCommercialAgr = React.useCallback(
    (
      row: Row,
      feeReceiver: string,
      utilityFees: string,
      billingPeriodMinutes?: string,
      rate?: string,
    ) => {
      const payload = {
        commercialAgreementCid: row.contractId as ContractId<CommercialAgreement>,
        feeReceiver,
        utilityFees: {
          credentialBillingFeeUsd: utilityFees,
          baseFee:
            !billingPeriodMinutes || !rate
              ? null
              : {
                  billingPeriodMinutes,
                  feePerDayUsd: {
                    rate,
                  },
                },
        },
      };
      return mutate(
        modifyCommercialAgreement,
        payload,
        SUC_MODIFY_COMMERCIAL_AGREEMENT,
        ERR_MODIFY_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, modifyCommercialAgreement],
  );

  const acceptCommercialAgreementOff = React.useCallback(
    (row: Row) => {
      const payload = {
        user: row.payload.user,
        commercialAgreementCid: row.contractId as ContractId<CommercialAgreementOffer>,
        utilityFees: { credentialBillingFeeUsd: row.payload.utilityFees, baseFee: null },
        operator: row.payload.operator,
        feeReceiver: row.payload.operator,
      };
      return mutate(
        acceptCommercialAgreementOffer,
        payload,
        SUC_ACCEPT_COMMERCIAL_AGREEMENT,
        ERR_ACCEPT_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, acceptCommercialAgreementOffer],
  );
  const cancelCommercialAgreementOff = React.useCallback(
    (row: Row) => {
      const payload = {
        commercialAgreementCid: row.contractId as ContractId<CommercialAgreementOffer>,
      };
      return mutate(
        cancelCommercialAgreementOffer,
        payload,
        SUC_CANCEL_COMMERCIAL_AGREEMENT,
        ERR_CANCEL_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, cancelCommercialAgreementOffer],
  );
  const revokeCommercialAgreementOff = React.useCallback(
    (row: Row) => {
      const payload = {
        commercialAgreementCid: row.contractId as ContractId<CommercialAgreement>,
        party,
      };
      return mutate(
        revokeCommercialAgreement,
        payload,
        SUC_REVOKE_COMMERCIAL_AGREEMENT,
        ERR_REVOKE_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, revokeCommercialAgreement, party],
  );
  const commercialAgreementLockC = React.useCallback(
    (row: Row, amount: string) => {
      const payload = {
        targetAmount: amount,
        commercialAgreementCid: row.contractId as ContractId<CommercialAgreement>,
      };
      return mutate(
        commercialAgreementLockCoin,
        payload,
        SUC_TOPUP_COMMERCIAL_AGREEMENT,
        ERR_TOPUP_COMMERCIAL_AGREEMENT,
      );
    },
    [mutate, commercialAgreementLockCoin],
  );
  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.state === 'Offered' && r.payload.user === party)
      actions.push(
        { name: 'Accept', action: () => Promise.resolve(acceptCommercialAgreementOff(r)) },
        {
          name: 'Reject',
          buttonProps: {
            variant: 'outlined',
            color: 'error',
          },
          action: () => {
            setOnReject(() => (reason: string) => rejectCommercialAgreementOff(r, reason));
            return Promise.resolve();
          },
        },
      );
    if (r.state === 'Offered' && isOperator) {
      actions.push({
        name: 'Cancel',
        action: cancelCommercialAgreementOff,
      });
    }
    if (r.state === 'Accepted') {
      if (r.payload.user === party) {
        actions.push({
          name: 'Top up',
          action: () => {
            setOnChangeTopupAmount(() => (amount: string) => commercialAgreementLockC(r, amount));
            return Promise.resolve();
          },
        });
      }

      if (isOperator) {
        actions.push({
          name: 'Modify',
          action: () => {
            setCurrFeeReceiver(r.payload.feeReceiver);
            setCurrUtilityFees(r.payload.utilityFees);
            setCurrRate(r.payload?.ratePerDay);
            setCurrBillingPeriodMinutes(r.payload?.billingPeriodMinutes);
            setOnModify(
              () =>
                (
                  feeReceiver: string,
                  utilityFees: string,
                  billingPeriodMinutes?: string,
                  rate?: string,
                ) =>
                  modifyCommercialAgr(r, feeReceiver, utilityFees, billingPeriodMinutes, rate),
            );
            return Promise.resolve();
          },
        });
      }
      actions.push({
        name: 'Revoke',
        buttonProps: {
          variant: 'outlined',
        },
        action: () => {
          setOnRevoke(() => () => revokeCommercialAgreementOff(r));
          return Promise.resolve();
        },
      });
    }
    return actions;
  };
  return {
    createActions,
    onReject,
    setOnReject,
    onModify,
    setOnModify,
    currFeeReceiver,
    currUtilityFees,
    currRate,
    currBillingPeriodMinutes,
    onChangeTopupAmount,
    setOnChangeTopupAmount,
    onRevoke,
    setOnRevoke,
  };
};
